/**
 * Centralized premium pronunciation profile for language-learning TTS.
 * Applied to browser SpeechSynthesis; keeps playback clearer, calmer, repeat-friendly.
 */

export interface PronunciationProfile {
  /** Speech rate (0.1–10). Slightly < 1 = slower, learner-friendly. */
  rate: number;
  /** Pitch (0–2). 1 = default; subtle values avoid robotic tone. */
  pitch: number;
  /** Pause in ms before repeating the same phrase. */
  pauseBeforeRepeatMs: number;
  /** Pause in ms between two pronunciations when repeat is enabled. */
  pauseBetweenRepeatsMs: number;
  /** 0 = no repeat, 1 = say twice (pronounce, pause, pronounce again). */
  repeatCount: number;
  /** Max length (chars) for content to be eligible for repeat. Longer = no repeat. */
  repeatMaxLength: number;
}

const DEFAULT_PROFILE: PronunciationProfile = {
  rate: 0.92,
  pitch: 1,
  pauseBeforeRepeatMs: 320,
  pauseBetweenRepeatsMs: 380,
  repeatCount: 1,
  repeatMaxLength: 120,
};

let currentProfile: PronunciationProfile = { ...DEFAULT_PROFILE };

/**
 * Returns the active pronunciation profile (learning-friendly defaults).
 */
export function getPronunciationProfile(): PronunciationProfile {
  return { ...currentProfile };
}

/**
 * Returns premium speech settings for use when building utterances.
 * Keeps explicit language + best voice selection; apply after resolving locale/voice.
 */
export function getPremiumSpeechSettings(): Pick<PronunciationProfile, "rate" | "pitch"> {
  const p = getPronunciationProfile();
  return { rate: p.rate, pitch: p.pitch };
}

/**
 * Whether the given text should be repeated (short enough, repeat enabled).
 */
export function shouldRepeatForLearning(text: string): boolean {
  const p = getPronunciationProfile();
  if (p.repeatCount < 1) return false;
  const trimmed = (text ?? "").trim();
  if (!trimmed) return false;
  return trimmed.length <= p.repeatMaxLength;
}

/**
 * Pause duration before repeating (ms).
 */
export function getPauseBeforeRepeatMs(): number {
  return getPronunciationProfile().pauseBeforeRepeatMs;
}

/**
 * Pause duration between repeated pronunciations (ms).
 */
export function getPauseBetweenRepeatsMs(): number {
  return getPronunciationProfile().pauseBetweenRepeatsMs;
}

/**
 * How many extra times to speak (0 = once, 1 = twice total).
 */
export function getRepeatCount(): number {
  return Math.max(0, getPronunciationProfile().repeatCount);
}
